import React, {
  FC,
  useState,
  useEffect,
  useCallback,
  useRef,
  FormEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { AgentEvents, ThreadActivityUpdateEvent } from '@dreamai/types';
import api from '../../api';
import { usePusher } from '../../contexts/PusherContext';
import { useAgent } from '../../contexts/AgentContext';
import { ReactComponent as PlusIcon } from '../../icons/plus.svg';
import { ReactComponent as ChevronDownIcon } from '../../icons/chevron-down.svg';
import { ReactComponent as ArchiveIcon } from '../../icons/empty-archive.svg';
import './channel-switcher.scss';

interface ChatChannel {
  threadId: string;
  name: string;
  isMain: boolean;
  unreadCount?: number;
}

interface ChannelSwitcherProps {
  threadId: string | null;
  onSelectChannel?: (threadId: string) => void;
}

/**
 * Chat menu: a pill in the chat panel header showing the current chat
 * ("General" or a custom chat), with a dropdown to switch between chats and
 * create new ones (capped server-side).
 */
const ChannelSwitcher: FC<ChannelSwitcherProps> = ({
  threadId,
  onSelectChannel,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const { activeAgent } = useAgent();
  const [channels, setChannels] = useState<ChatChannel[]>([]);
  const [maxChannels, setMaxChannels] = useState(4);
  const [open, setOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<ChatChannel | null>(null);
  const [isArchiving, setIsArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const channelsRef = useRef<ChatChannel[]>([]);
  channelsRef.current = channels;

  const fetchChannels = useCallback(async (): Promise<ChatChannel[]> => {
    try {
      const token = await getAccessTokenSilently();
      const query = new URLSearchParams();
      if (threadId) query.set('thread_id', threadId);
      else if (activeAgent?.id) query.set('agent_id', activeAgent.id);
      const response = await api.get(`/chat-channels?${query.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setChannels(response.data.channels);
        if (response.data.maxChannels) {
          setMaxChannels(response.data.maxChannels);
        }
        return response.data.channels;
      }
    } catch (error) {
      console.error('Error fetching chat channels:', error);
    }
    return [];
  }, [getAccessTokenSilently, activeAgent?.id, threadId]);

  useEffect(() => {
    fetchChannels();
  }, [fetchChannels]);

  // Refresh the chat list when a thread is created or archived elsewhere
  // (e.g. the assistant creating a chat via createChatChannel or archiving
  // one via archiveChatChannel). If the chat currently being viewed was
  // archived, fall back to General.
  const pusherContext = usePusher(true);
  const userChannel = pusherContext?.userChannel;
  const companyChannel = pusherContext?.pusherChannel;
  useEffect(() => {
    if (!userChannel) return;
    const handleThreadCreated = () => {
      fetchChannels();
    };
    const handleThreadArchived = async (data?: { threadId?: string }) => {
      const freshChannels = await fetchChannels();
      if (data?.threadId && data.threadId === threadId) {
        const general = freshChannels.find((c) => c.isMain);
        if (general && onSelectChannel) onSelectChannel(general.threadId);
      }
    };
    userChannel.bind(AgentEvents.THREAD_CREATED, handleThreadCreated);
    userChannel.bind(AgentEvents.THREAD_ARCHIVED, handleThreadArchived);
    return () => {
      userChannel.unbind(AgentEvents.THREAD_CREATED, handleThreadCreated);
      userChannel.unbind(AgentEvents.THREAD_ARCHIVED, handleThreadArchived);
    };
  }, [userChannel, fetchChannels, threadId, onSelectChannel]);

  // Refresh unread counts when activity lands on one of the chat threads
  // (fires when an agent message completes). Debounced to avoid hammering
  // the API during bursts of activity.
  useEffect(() => {
    if (!companyChannel) return;
    const handleThreadActivity = (data?: ThreadActivityUpdateEvent) => {
      if (
        data?.thread_id &&
        !channelsRef.current.some((c) => c.threadId === data.thread_id)
      ) {
        return;
      }
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      refreshTimeoutRef.current = setTimeout(() => {
        fetchChannels();
      }, 500);
    };
    companyChannel.bind(
      AgentEvents.THREAD_ACTIVITY_UPDATE,
      handleThreadActivity,
    );
    return () => {
      companyChannel.unbind(
        AgentEvents.THREAD_ACTIVITY_UPDATE,
        handleThreadActivity,
      );
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [companyChannel, fetchChannels]);

  // Close the dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  useEffect(() => {
    if (showCreateModal) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [showCreateModal]);

  const currentChannel =
    channels.find((c) => c.threadId === threadId) ||
    channels.find((c) => c.isMain);

  // Unread counts: the channel being viewed is by definition read (the app
  // marks it as read while the user is looking at it), so it never shows a
  // badge. The pill badge is the total across the other channels.
  const unreadCountFor = (channel: ChatChannel): number =>
    channel.threadId === currentChannel?.threadId
      ? 0
      : channel.unreadCount || 0;
  const totalUnread = channels.reduce(
    (sum, channel) => sum + unreadCountFor(channel),
    0,
  );

  const navigateToChannel = (channel: ChatChannel) => {
    setOpen(false);
    if (channel.threadId === threadId) return;
    if (onSelectChannel) {
      onSelectChannel(channel.threadId);
      return;
    }
    const params = new URLSearchParams(window.location.search);
    params.set('thread_id', channel.threadId);
    params.delete('new');
    window.location.search = params.toString();
  };

  const openCreateModal = () => {
    setOpen(false);
    setNewName('');
    setCreateError(null);
    setShowCreateModal(true);
  };

  const handleCreate = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!newName.trim() || isCreating) return;

    setIsCreating(true);
    setCreateError(null);
    try {
      const token = await getAccessTokenSilently();
      const response = await api.post(
        '/chat-channels',
        {
          name: newName.trim(),
          ...(threadId
            ? { thread_id: threadId }
            : { agent_id: activeAgent?.id }),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data.success && response.data.channel) {
        setShowCreateModal(false);
        await fetchChannels();
        navigateToChannel(response.data.channel);
      }
    } catch (error: any) {
      setCreateError(
        error.response?.data?.error || 'Failed to create the chat.',
      );
    } finally {
      setIsCreating(false);
    }
  };

  const openArchiveModal = (channel: ChatChannel) => {
    setOpen(false);
    setArchiveError(null);
    setArchiveTarget(channel);
  };

  const handleArchive = async () => {
    if (!archiveTarget || isArchiving) return;
    setIsArchiving(true);
    setArchiveError(null);
    try {
      const token = await getAccessTokenSilently();
      await api.post(
        `/chat-channels/${archiveTarget.threadId}/archive`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const archivedId = archiveTarget.threadId;
      setArchiveTarget(null);
      const freshChannels = await fetchChannels();
      if (archivedId === threadId) {
        const general = freshChannels.find((c) => c.isMain);
        if (general) navigateToChannel(general);
      }
    } catch (error: any) {
      setArchiveError(
        error.response?.data?.error || 'Failed to archive the chat.',
      );
    } finally {
      setIsArchiving(false);
    }
  };

  const atCap = channels.length >= maxChannels;

  // Only show the chat menu on chat surfaces (General / custom chats) — not
  // on project, task, or other special threads.
  if (
    channels.length === 0 ||
    (threadId && !channels.some((c) => c.threadId === threadId))
  ) {
    return null;
  }

  return (
    <div
      className={`channel-switcher${open ? ' open' : ''}`}
      ref={containerRef}
    >
      <button
        type='button'
        className='channel-switcher-pill'
        onClick={() => setOpen(!open)}
        aria-haspopup='menu'
        aria-expanded={open}
      >
        <span className='channel-switcher-pill-icon'>#</span>
        <span className='channel-switcher-pill-name'>
          {currentChannel?.name || 'General'}
        </span>
        <span
          className={`channel-switcher-pill-chevron${
            totalUnread > 0 ? ' has-unread' : ''
          }`}
          aria-hidden='true'
        >
          {totalUnread > 0 && (
            <span className='channel-switcher-pill-chevron-count'>
              {totalUnread}
            </span>
          )}
          <ChevronDownIcon />
        </span>
      </button>

      {open && (
        <div className='channel-switcher-menu' role='menu'>
          <div className='channel-switcher-menu-list'>
            {channels.map((channel) => (
              <div
                key={channel.threadId}
                role='menuitem'
                tabIndex={0}
                className={`channel-switcher-item${
                  currentChannel?.threadId === channel.threadId ? ' active' : ''
                }`}
                onClick={() => navigateToChannel(channel)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    navigateToChannel(channel);
                  }
                }}
              >
                <span className='channel-switcher-item-icon'>#</span>
                <span className='channel-switcher-item-name'>
                  {channel.name}
                </span>
                {unreadCountFor(channel) > 0 && (
                  <span className='channel-switcher-badge channel-switcher-item-badge'>
                    {unreadCountFor(channel)}
                  </span>
                )}
                {!channel.isMain && (
                  <button
                    type='button'
                    className='channel-switcher-item-archive'
                    aria-label={`Archive ${channel.name}`}
                    title='Archive chat'
                    onClick={(e) => {
                      e.stopPropagation();
                      openArchiveModal(channel);
                    }}
                  >
                    <ArchiveIcon />
                  </button>
                )}
              </div>
            ))}
            {channels.length <= 1 && (
              <div className='channel-switcher-empty'>
                Use chats for focussed conversations
              </div>
            )}
          </div>
          <div
            className={`channel-switcher-new-wrap${atCap ? ' disabled' : ''}`}
            data-tooltip={
              atCap
                ? 'Currently four channels is the limit on your account'
                : undefined
            }
          >
            <button
              type='button'
              role='menuitem'
              className='channel-switcher-new'
              onClick={openCreateModal}
              disabled={atCap}
            >
              <PlusIcon />
              <span>New chat</span>
            </button>
          </div>
        </div>
      )}

      {archiveTarget &&
        createPortal(
          <div
            className='channel-create-backdrop'
            onClick={() => setArchiveTarget(null)}
          >
            <div
              className='channel-create-modal'
              onClick={(e) => e.stopPropagation()}
            >
              <h2>Archive “{archiveTarget.name}”?</h2>
              <p className='channel-create-hint'>
                Archiving this chat means your secretary can still re-read its
                content when asked, but won&apos;t automatically consider it in
                other conversations.
              </p>
              {archiveError && (
                <div className='channel-create-error'>{archiveError}</div>
              )}
              <div className='channel-create-actions'>
                <button
                  type='button'
                  className='channel-create-cancel'
                  onClick={() => setArchiveTarget(null)}
                >
                  Cancel
                </button>
                <button
                  type='button'
                  className='channel-create-submit'
                  disabled={isArchiving}
                  onClick={handleArchive}
                >
                  {isArchiving ? 'Archiving…' : 'Archive chat'}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {showCreateModal &&
        createPortal(
          <div
            className='channel-create-backdrop'
            onClick={() => setShowCreateModal(false)}
          >
            <div
              className='channel-create-modal'
              onClick={(e) => e.stopPropagation()}
            >
              <h2>New chat</h2>
              <form onSubmit={handleCreate}>
                <label htmlFor='channel-create-title'>Chat title</label>
                <input
                  id='channel-create-title'
                  ref={inputRef}
                  type='text'
                  className={createError ? 'has-error' : ''}
                  placeholder='eg. finance, or travel-planning'
                  value={newName}
                  maxLength={60}
                  onChange={(e) => {
                    setNewName(e.target.value);
                    setCreateError(null);
                  }}
                />
                {createError && (
                  <div className='channel-create-error'>{createError}</div>
                )}
                <p className='channel-create-hint'>
                  Create new chats to keep specific discussions in one place.
                  Your secretary can remember information across chats.
                </p>
                <div className='channel-create-actions'>
                  <button
                    type='button'
                    className='channel-create-cancel'
                    onClick={() => setShowCreateModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type='submit'
                    className='channel-create-submit'
                    disabled={!newName.trim() || isCreating}
                  >
                    {isCreating ? 'Creating…' : 'Create new chat'}
                    <PlusIcon />
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default ChannelSwitcher;
